import { PropertyDetailsFeeCalculatorProps } from '@greystar/ui';
import { useRouter } from 'next/router';
import { GetStaticComponentProps } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useRef, useState, useReducer, useEffect, useMemo, JSX } from 'react';
import {
  PageSectionContent,
  LayoutContainer,
  PageSection,
  Grid,
  ApplyInterstitialModal,
} from '@greystar/designsystem';
import UnitDetails from '@greystar/ui/src/components/FeeCalc/UnitDetails/UnitDetails';
import CalculateStep from '@greystar/ui/src/components/FeeCalc/Steps/CalculateStep/CalculateStep';
import FloorPlanImg from '@greystar/ui/src/components/FeeCalc/FloorPlanImg/FloorPlanImg';
import PricingTray from '@greystar/ui/src/components/FeeCalc/PricingTray/PricingTray';
import SummaryStep from '@greystar/ui/src/components/FeeCalc/Steps/SummaryStep/SummaryStep';
import {
  feeReducer,
  initialFeeState,
  setFees,
  setSelectedLeaseTerm,
  setSelectedMoveInDate,
  Property,
  setSelectedUnit,
  setNumberOfApplicants,
  extendAvailableUnits,
  reduceAvailableUnits,
  FeesAnalyticsDataProvider,
  useFeesAnalytics,
  getFeesAnalyticsData,
  setFeesIdMapper,
  useAnalytics,
  deleteUndefined,
  getOneTimeStepOneFees,
  getMonthlyFeeGroups,
  getMoveinDate,
  backToFpe,
  applyNow,
  closeModal,
} from '@greystar/lib';
import { AvailableUnitExtended, MonthlyFees } from '@greystar/lib/src/types/property-types';

// params from floor plan list
type FeeCalcParams = {
  selectedUnitId: string;
  selectedMoveInDate: string;
  selectedLeaseTerm: string;
};

const PropertyDetailsFeeCalculator = (props: PropertyDetailsFeeCalculatorProps): JSX.Element => {
  const router = useRouter();
  const [activeStep, setActiveStep] = useState(1);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const [feeState, dispatch] = useReducer(feeReducer, initialFeeState);
  const property = props.data.property;
  const analytics = useAnalytics();
  const { setAnalyticsData, mergeAnalyticsData } = useFeesAnalytics();
  const hasTrackedRef = useRef(false);
  const initialAnalyticsData = getFeesAnalyticsData(property as Property, feeState, activeStep);

  useEffect(() => {
    if (!router.isReady || !property) return;

    const queryUnitId = router.query.unitId as string;
    if (queryUnitId) {
      const isQueryIdValid = property.availableUnits?.some((unit) => unit.unitId === queryUnitId);

      if (!isQueryIdValid) {
        backToFpe(router, 'fpe-floor-plans');
      }
    }
  }, [router, property]);

  const calcParams: FeeCalcParams | null = useMemo(() => {
    if (!router.isReady || !property) {
      return null;
    }

    const availableUnits = property.availableUnits ?? [];
    const queryUnitId = router.query.unitId as string;
    let activeUnitId = availableUnits[0]?.unitId ?? '';

    const isQueryIdValid = availableUnits.some((unit) => unit.unitId === queryUnitId);
    if (queryUnitId && isQueryIdValid) {
      activeUnitId = queryUnitId;
    }

    return {
      selectedUnitId: activeUnitId,
      selectedMoveInDate: (router.query?.moveInDate as string) ?? '',
      selectedLeaseTerm: (router.query?.leaseTerm as string) ?? '12',
    };
  }, [property, router.isReady, router.query]);

  // load selected unit
  const selectedUnit = useMemo(() => {
    if (!calcParams) return null;

    return property?.availableUnits?.find(
      (unit: AvailableUnitExtended) => unit.unitId == calcParams.selectedUnitId
    ) as AvailableUnitExtended;
  }, [calcParams, property]);

  useEffect(() => {
    const isReady =
      property &&
      selectedUnit &&
      feeState.selectedUnit &&
      feeState.selectedMoveInDate &&
      feeState.selectedLeaseTerm;

    if (!isReady) return;

    if (!hasTrackedRef.current) {
      const oneTimeFees = Array.from(getOneTimeStepOneFees(feeState.allFees).values()).flat();
      const monthlyFees = Array.from(getMonthlyFeeGroups(feeState.allFees).values()).flat();

      setFeesIdMapper([...oneTimeFees, ...monthlyFees]);
    }

    setAnalyticsData(initialAnalyticsData);

    if (initialAnalyticsData && !hasTrackedRef.current) {
      mergeAnalyticsData({
        overrides: {
          event: 'unit_rent_calc_impression',
          action_type: 'pageview',
        },
        onComplete: (data) => {
          analytics.trackEvent('unit_rent_calc_impression', data);
          hasTrackedRef.current = true;
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property, selectedUnit, feeState]);

  useEffect(() => {
    if (!property || !selectedUnit || !calcParams) return;

    //handle the type assertion when we get the query params and the reqs are clear
    setSelectedUnit(dispatch)(selectedUnit);

    setSelectedLeaseTerm(dispatch)(calcParams.selectedLeaseTerm);
    const moveInDate = getMoveinDate(calcParams.selectedMoveInDate, selectedUnit);
    setSelectedMoveInDate(dispatch)(moveInDate);
    setFees(dispatch)(property.fees || []);
  }, [calcParams, property, selectedUnit]);

  if (!props.data) {
    return <></>;
  }

  if (!props.data.property || !router.isReady) return <div>Loading property data...</div>;

  const scrollToTopOfRef = () => {
    if (ref.current) {
      const elementPosition = ref.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - 16;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  if (!props.data || !props.data.property || !selectedUnit) {
    return <div>Loading property data...</div>;
  }

  return (
    <div>
      <PageSection topColour="beige" isTopFlushed>
        <LayoutContainer paddingBottom="md">
          <PageSectionContent hasMaxWidth>
            <LayoutContainer flexDirection="column" gap="xl">
              <UnitDetails
                property={property as Property}
                selectedUnit={selectedUnit}
                setIsApplyModalOpen={setIsApplyModalOpen}
                analytics={initialAnalyticsData}
              />
            </LayoutContainer>
          </PageSectionContent>
        </LayoutContainer>
      </PageSection>

      <PageSection id="fee-calculator">
        <PageSectionContent hasMaxWidth>
          <LayoutContainer flexDirection="column" gap="xl" paddingTop="1xl">
            <Grid>
              <Grid.Item smColumnSpan={4} mdColumnSpan={5} lgColumnSpan={7} smRow={2}>
                <section ref={ref}>
                  {activeStep === 1 ? (
                    <CalculateStep
                      selectedUnit={selectedUnit}
                      feeState={feeState}
                      dispatch={dispatch}
                      setSelectedMoveInDate={setSelectedMoveInDate}
                      setSelectedLeaseTerm={setSelectedLeaseTerm}
                      setNumberOfApplicants={setNumberOfApplicants}
                    />
                  ) : (
                    <SummaryStep
                      selectedUnit={selectedUnit}
                      feeState={feeState}
                      dispatch={dispatch}
                      setSelectedMoveInDate={setSelectedMoveInDate}
                      setSelectedLeaseTerm={setSelectedLeaseTerm}
                    />
                  )}
                </section>
              </Grid.Item>
              <Grid.Item smColumnSpan={4} mdColumnSpan={3} lgColumnSpan={5} smRow={1}>
                <FloorPlanImg floorPlanImg={selectedUnit?.floorplan?.imageUrl ?? ''} />
              </Grid.Item>
            </Grid>
          </LayoutContainer>
        </PageSectionContent>
      </PageSection>
      <PricingTray
        {...props.data.property}
        feeState={feeState}
        dispatch={dispatch}
        onNavigate={scrollToTopOfRef}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        setIsApplyModalOpen={setIsApplyModalOpen}
        analytics={initialAnalyticsData}
      />
      {isApplyModalOpen && (
        <ApplyInterstitialModal
          isOpen={isApplyModalOpen}
          onClose={() => {
            closeModal({
              setIsModalOpen: setIsApplyModalOpen,
              trackEvent: analytics.trackEvent,
              eventData: {
                ...initialAnalyticsData,
              },
              applyNowUrl: property?.contact?.leaseOnlineUrl,
              eventName: 'unit_rent_calc_interstitial_close',
            });
          }}
          onAccept={() => {
            applyNow({
              router,
              setIsModalOpen: setIsApplyModalOpen,
              applyNowUrl: property?.contact?.leaseOnlineUrl,
              trackEvent: analytics.trackEvent,
              eventName: 'unit_rent_calc_apply',
              eventData: {
                ...initialAnalyticsData,
                action_type: 'entry',
                click_text: 'Continue',
                destination_url: property?.contact?.leaseOnlineUrl ?? '',
              },
            });
          }}
        />
      )}
    </div>
  );
};

export const getStaticProps: GetStaticComponentProps = async (
  rendering,
  layoutData
): Promise<PropertyDetailsFeeCalculatorProps> => {
  const props = rendering as unknown as PropertyDetailsFeeCalculatorProps;
  const property = layoutData.sitecore.context.property as Property & MonthlyFees;

  if (!property) {
    return {
      ...props,
      data: {
        property: null,
        notFound: true,
      },
    };
  }
  const monthlyFees: MonthlyFees = {
    requiredMonthlyFeesMin: property.requiredMonthlyFeesMin,
    requiredMonthlyFeesMax: property.requiredMonthlyFeesMax,
  };
  property.availableUnits = extendAvailableUnits({
    floorplans: property.floorplans,
    monthlyFees,
    units: property.availableUnits,
  });

  return {
    ...props,
    data: {
      property: deleteUndefined<Property>({
        id: property.id,
        name: property.name,
        availableUnits: reduceAvailableUnits({
          units: property.availableUnits,
          removeRentMatrix: true,
        }),
        fees: property.fees,
        contact: property.contact,
        location: property.location,
      }),
      notFound: false,
    },
  };
};

// export default PropertyDetailsFeeCalculator;

const FeeCalculatorWrapper = (props: PropertyDetailsFeeCalculatorProps): JSX.Element => {
  return (
    <FeesAnalyticsDataProvider initialData={{ module_name: 'fee transparency calculator' }}>
      <PropertyDetailsFeeCalculator {...props} />
    </FeesAnalyticsDataProvider>
  );
};

export default FeeCalculatorWrapper;
